import { Checkbox } from '@/shared/ui/atoms';
import type { CandidateRow, CandidateStatus } from '@/entities/outreach-candidate';
import * as s from './CandidateTable.css';

export interface CandidateTableProps {
  rows: CandidateRow[];
  /** 고른 후보 — `externalCreatorId` 다. 핸들이 아니다(핸들은 바뀐다). */
  selectedIds: ReadonlySet<string>;
  /**
   * 후보별 최근 접촉. **아직 안 왔으면 `null`** 이다 — 빈 Map 으로 주면 전원이 `미확인` 으로
   * 그려져, 이미 회신한 사람에게 다시 보내는 판단이 된다.
   */
  statuses: ReadonlyMap<string, CandidateStatus> | null;
  onToggle: (externalCreatorId: string) => void;
  /** 머리줄 체크박스 — 이 표에 보이는 행을 모두 고르거나(`true`) 모두 푼다(`false`). */
  onToggleAll: (picked: boolean) => void;
  /** 로스터 행으로 가는 링크. 폼 제출 행만 쓴다(IA §3-4). */
  rosterHref?: (rosterEntryId: string) => string;
}

const numberFormat = new Intl.NumberFormat('ko-KR');

function formatDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

/**
 * 후보 표 — IA `docs/design/ia/creator-recruitment-ia.md` §3-3 「행 한 줄」.
 *
 * 칸 넷이다: 선택 · 크리에이터(핸들) · 팔로워 · 리전 · 최근 접촉. 조회수 지표는 이 표가 아니라
 * 행 아래 한 줄이고(§3-3, 퀘스트 `aad3fb38`) 아직 이 화면에 안 붙었다.
 *
 * **최근 접촉이 아직 안 왔을 때 `미확인` 으로 그리지 않는다.** 둘은 다른 뜻이다 — 앞엣것은
 * 물어보는 중이고 뒤엣것은 물어봤는데 연결이 없는 것이다. 합치면 회신한 사람에게 다시 보낸다.
 */
export function CandidateTable({
  rows,
  selectedIds,
  statuses,
  onToggle,
  onToggleAll,
  rosterHref,
}: CandidateTableProps) {
  // 주소 없는 행은 메일로 못 보내 고를 수 없다 — 머리줄 체크박스도 고를 수 있는 행만 센다.
  const pickable = rows.filter((row) => row.hasAddress !== false);
  const pickedCount = pickable.filter((row) => selectedIds.has(row.externalCreatorId)).length;
  const allPicked = pickable.length > 0 && pickedCount === pickable.length;

  return (
    /*
      표는 자기 칸 안에서 스크롤한다(2026-09-29). 면 전체의 스크롤을 같이 쓰면 한 페이지(최대
      500행) 아래의 「지금 답할 회신」·「발송 이력」 이 수천 픽셀 밑으로 밀린다.
    */
    <div className={s.scroller}>
      <table className={s.table}>
        <caption className={s.caption}>조건에 맞는 후보</caption>
        <thead>
          <tr>
            <th scope="col" className={s.pickHead}>
              <Checkbox
                checkboxSize="sm"
                checked={allPicked}
                indeterminate={pickedCount > 0 && !allPicked}
                disabled={pickable.length === 0}
                onChange={() => onToggleAll(!allPicked)}
                aria-label="이 표의 후보 모두 고르기"
              />
            </th>
            <th scope="col" className={s.th}>
              크리에이터
            </th>
            <th scope="col" className={`${s.th} ${s.numeric}`}>
              팔로워
            </th>
            <th scope="col" className={s.th}>
              리전
            </th>
            <th scope="col" className={s.th}>
              최근 접촉
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const status = statuses?.get(row.externalCreatorId) ?? null;
            return (
              <tr key={row.externalCreatorId} className={s.row}>
                <td className={s.pickCell}>
                  <Checkbox
                    checkboxSize="sm"
                    checked={selectedIds.has(row.externalCreatorId)}
                    disabled={row.hasAddress === false}
                    onChange={() => onToggle(row.externalCreatorId)}
                    aria-label={`${row.handle} 고르기`}
                  />
                </td>
                <td className={s.td}>
                  <span className={s.handle}>{row.handle}</span>
                  {row.hasAddress === false ? (
                    <span className={s.muted}> · 주소 없음 — 다른 매체로 섭외</span>
                  ) : null}
                </td>
                <td className={`${s.td} ${s.numeric}`}>
                  {row.followers === null ? (
                    <span className={s.muted}>모름</span>
                  ) : (
                    numberFormat.format(row.followers)
                  )}
                </td>
                <td className={s.td}>
                  {row.region === null ? <span className={s.muted}>미상</span> : row.region}
                </td>
                <td className={s.td}>
                  <ContactCell status={status} rosterHref={rosterHref} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ContactCell({
  status,
  rosterHref,
}: {
  status: CandidateStatus | null;
  rosterHref?: (rosterEntryId: string) => string;
}) {
  // 아직 안 온 것이지 미확인이 아니다.
  if (status === null) return <span className={s.muted}>불러오는 중</span>;

  switch (status.state) {
    case 'form_submitted':
      return status.rosterEntryId !== null && rosterHref !== undefined ? (
        <a className={s.rosterLink} href={rosterHref(status.rosterEntryId)}>
          폼 제출 · 로스터 행 보기
        </a>
      ) : (
        <span className={s.form}>폼 제출</span>
      );
    case 'replied':
      return (
        <span className={s.replied}>
          회신 있음{status.at === null ? '' : ` · ${formatDay(status.at)}`}
        </span>
      );
    case 'no_reply':
      return <span className={s.noReply}>회신 없음</span>;
    case 'unknown':
      return <span className={s.muted}>미확인</span>;
  }
}
